import { HttpEvent, HttpHandlerFn, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { inject, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, switchMap, tap, filter, take } from 'rxjs/operators';
import { AuthRoutesService } from '../service/auth-routes.service';
import { AuthService } from '../service/auth.service';
import { Router } from '@angular/router';
import { RefreshTokenResponse } from '../interfaces/user/user.interface';

// Variable global para controlar el refresh del token
let isRefreshing = false;
let refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

export function tokenInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> {
  const authService = inject(AuthService);
  const authRoutesService = inject(AuthRoutesService);
  const router = inject(Router);
  const platformId = inject(PLATFORM_ID);
  const isBrowser = isPlatformBrowser(platformId);

  // Verificar si la ruta actual es pública
  const isPublicRoute = authRoutesService.isPublicUrl(req.url);

  // Solo añadir el token si NO es una ruta pública
  if (!isPublicRoute) {
    const token = authService.getToken();
    if (token) {
      const authReq = req.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`
        }
      });

      return next(authReq).pipe(
        catchError((error: HttpErrorResponse) => {
          // Manejar errores de autenticación (token expirado)
          // Verificar tanto 401
          const isTokenExpired = error.status === 401;

          if (isTokenExpired) {
            console.log('Token expirado detectado:', {
              status: error.status,
              message: error.error?.message,
              url: req.url
            });

            if (!isRefreshing) {
              isRefreshing = true;
              refreshTokenSubject.next(null);

              return authService.refreshToken().pipe(
                switchMap((res: RefreshTokenResponse) => {
                  isRefreshing = false;
                  authService.setToken(res.data.token);
                  refreshTokenSubject.next(res.data.token);

                  const newReq = req.clone({
                    setHeaders: {
                      Authorization: `Bearer ${res.data.token}`
                    }
                  });
                  // console.log('Token refrescado exitosamente');
                  return next(newReq);
                }),
                catchError((refreshError) => {
                  isRefreshing = false;
                  console.log('Error al refrescar token:', refreshError);
                  // Limpiar autenticación y redirigir solo en navegador
                  authService.clearAuth();
                  if (isBrowser) {
                    router.navigate(['/']);
                  }
                  return throwError(() => refreshError);
                })
              );
            } else {
              // Si ya se está refrescando, esperar a que termine
              return refreshTokenSubject.pipe(
                filter(token => token !== null),
                take(1),
                switchMap(token => {
                  const newReq = req.clone({
                    setHeaders: {
                      Authorization: `Bearer ${token}`
                    }
                  });
                  return next(newReq);
                })
              );
            }
          }
          return throwError(() => error);
        })
      );
    }
  }

  // Para rutas públicas o sin token, continuar sin modificar
  return next(req);
}
